/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { FC, useEffect, useMemo } from 'react';

import Link from 'next/link';

import { staticColors } from '~/common/styles/theme';
import { IToastProps } from '~/common/interfaces/IToastProps';
import { useMediaQuery } from '~/common/hooks/use-media-query';
import { TAlertToastTypes } from '~/common/@types/AlertToastTypes';

import {
  BottomInfoContainer,
  CloseWrapper,
  DescriptionWrapper,
  ToastContainer,
  ToastMainInfoRow,
} from './styles';
import { Icon } from '../Icons';

const icons: Record<TAlertToastTypes, string> = {
  error: 'error-warning-fill',
  warning: 'alert-fill',
  success: 'checkbox-circle-fill',
  info: 'information-fill',
  neutral: 'information-fill',
};

/**
 * @todo - integrar com a tipografia
 * @todo - passar o onClick de fechar para o onClick do icone
 * @tood - ajustar bug de timer após fechar manualmente
 */

export const AlertToast: FC<IToastProps> = ({
  visible,
  type,
  color,
  iconColor,
  description,
  title,
  customLinkText,
  customLinkHref,
  withKnowMore = false,
  withUpdate = false,
  asBanner = false,
  duration = 2000,
  position = 'top-right',
  forceClose = () => null,
  customLinkClickAction,
}) => {
  const renderingOnMobile = useMediaQuery('(max-width: 750px)');

  const singleText = useMemo(
    () =>
      (!!title && !description) || (!!description && !title)
        ? description || title
        : null,
    [description, title]
  );

  const hyperlinkText = useMemo(
    () => customLinkText || (withUpdate ? 'Update' : null),
    [withUpdate, customLinkText]
  );

  const hyperlinkHref = useMemo(
    () => customLinkHref || (withUpdate ? '/update' : null),
    [customLinkHref, withUpdate]
  );

  const renderKnowMoreOrWithUpdateOnBottom = useMemo(
    () => (withKnowMore || hyperlinkText) && (!singleText || renderingOnMobile),
    [withKnowMore, hyperlinkText, singleText, renderingOnMobile]
  );

  const Hyperlink = useMemo(() => {
    if (hyperlinkHref) {
      return (
        <Link href={hyperlinkHref} passHref>
          <span
            style={{
              color: staticColors.grey[0],
              textDecoration: 'underline',
            }}
          >
            {hyperlinkText}
          </span>
        </Link>
      );
    }

    if (customLinkClickAction) {
      return (
        <span
          onClick={customLinkClickAction}
          style={{
            color: staticColors.grey[0],
            textDecoration: 'underline',
          }}
        >
          {hyperlinkText}
        </span>
      );
    }
  }, [hyperlinkHref, hyperlinkText, customLinkClickAction]);

  useEffect(() => {
    if (visible) {
      setTimeout(() => {
        forceClose();
      }, duration);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, duration]);

  const renderBottomContent = useMemo(
    () =>
      renderingOnMobile ||
      (!asBanner && (!!hyperlinkText || withKnowMore || description)),
    [asBanner, hyperlinkText, renderingOnMobile, description, withKnowMore]
  );

  const renderLinkAtMainRow = useMemo(() => {
    if (!renderingOnMobile) {
      return !!(singleText || asBanner) && hyperlinkText;
    }

    return !!hyperlinkText && !renderBottomContent;
  }, [
    asBanner,
    hyperlinkText,
    renderBottomContent,
    renderingOnMobile,
    singleText,
  ]);

  return (
    <ToastContainer
      visible={visible}
      backgroundColor={color}
      asBanner={renderingOnMobile ? false : asBanner}
      position={
        asBanner || renderingOnMobile
          ? position.includes('top')
            ? 'top'
            : 'bottom'
          : position
      }
    >
      <ToastMainInfoRow asBanner={renderingOnMobile ? false : asBanner}>
        <Icon type={icons[type] || ''} color={iconColor} size={20} />
        <span>{singleText || title}</span>
        {!singleText && asBanner && !!description && !renderingOnMobile && (
          <>
            <span>. {description}</span>
          </>
        )}
        <div id="right">
          {renderLinkAtMainRow && <>{Hyperlink}</>}

          <CloseWrapper onClick={forceClose}>
            <Icon type="close-line" color={staticColors.grey[0]} size={20} />
          </CloseWrapper>
        </div>
      </ToastMainInfoRow>

      {renderBottomContent && (
        <>
          {!singleText && !!description && (
            <DescriptionWrapper>
              <span>{description}</span>
            </DescriptionWrapper>
          )}

          {renderKnowMoreOrWithUpdateOnBottom && (
            <BottomInfoContainer>
              {hyperlinkText && <>{Hyperlink}</>}

              {hyperlinkText && withKnowMore && (
                <span
                  style={{
                    color: staticColors.grey[0],
                    opacity: 0.75,
                    marginBottom: 'auto',
                  }}
                >
                  .
                </span>
              )}

              {withKnowMore && (
                <Link href="/knowmore" passHref>
                  <span
                    id="upgrade"
                    style={{
                      color: staticColors.grey[0],
                    }}
                  >
                    Saiba mais
                  </span>
                </Link>
              )}
            </BottomInfoContainer>
          )}
        </>
      )}
    </ToastContainer>
  );
};
